import { Component, Input, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { getListAsync, groupTypesRadioList, innerScrollEl, ITEMS_LIMIT, returnScrollPosition, scrollToSelected } from 'src/app/app.utils';
import { HelperProvider } from 'src/app/providers/helper/helper';
import { GroupType, GroupCreateFields, Group } from 'src/app/model';
import { DmStoreService } from 'src/app/store/dm-store.service';
import { AlertController, IonicModule } from '@ionic/angular';
import { ApiService } from 'src/app/providers/api/api.service';
import { Subscription } from 'rxjs';
import { IonicSelectableComponent, IonicSelectableFooterTemplateDirective, IonicSelectablePlaceholderTemplateDirective } from 'ionic-selectable';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-group-select',
  templateUrl: './group-select.component.html',
  styleUrls: ['./group-select.component.scss'],
  standalone: true,
  imports: [IonicModule, FormsModule, CommonModule, IonicSelectableComponent, IonicSelectableFooterTemplateDirective, IonicSelectablePlaceholderTemplateDirective],
})
export class GroupSelectComponent implements OnInit, OnDestroy {
  private groupsSub: Subscription;
  private zonesSub: Subscription;
  private levelsSub: Subscription;

  private page = 1;

  private ZoneId: number | undefined;
  private GroupId: number | undefined;

  public groups$ = this.dmStore.groups$;

  @ViewChild('groupsSelectComponent') groupsSelectComponent: IonicSelectableComponent;

  get groupId(): number | undefined {
    return this.GroupId;
  }
  @Input()
  set groupId(val: number | undefined) {
    this.GroupId = val;
  }

  @Input()
  set zoneId(val: number | undefined) {
    this.ZoneId = val;
  }
  get zoneId(): number | undefined {
    return this.ZoneId;
  }

  @Input() disabled: boolean;

  constructor(
    private dmStore: DmStoreService,
    private alertController: AlertController,
    private api: ApiService,
    private helper: HelperProvider,
  ) { }

  ngOnInit() {
    this.zonesSub = this.dmStore.selectedZone$.subscribe(zone => {
      this.page = 1;
      this.groupId = undefined;
      if (zone) {
        this.zoneId = zone.id;
      } else {
        this.zoneId = undefined;
      }
    });

    this.levelsSub = this.dmStore.selectedLevel$.subscribe(() => {
      this.page = 1;
      this.groupId = undefined;
    });

    this.groupsSub = this.dmStore.selectedGroup$.subscribe(group => {
      this.groupId = group?.id || undefined;
    });
  }

  ngOnDestroy() {
    this.groupsSub.unsubscribe();
    this.zonesSub.unsubscribe();
    this.levelsSub.unsubscribe();
  }

  public selectGroup() {
    this.groupsSelectComponent.confirm();
    this.groupsSelectComponent.close();

    this.dmStore.preselectedValuesClear();

    this.dmStore.selectGroup(this.groupId);
  }

  public async createGroup() {
    if (!this.zoneId) {
      return;
    }

    const zone_id = this.zoneId;

    this.groupsSelectComponent.close();
    const groupnameAlert = await this.alertController.create({
      header: 'Group name',
      inputs: [
        {
          name: 'name',
          placeholder: 'Name',
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Next',
          handler: ({ name }: { name: string }) => {
            if (name.trim()?.length) {
              this.choseGroupType({ name, type: GroupType.MarkedParkingBay, zone_id });
            } else {
              return false;
            }
            return true;
          },
        },
      ],
    });

    groupnameAlert.present();
  }

  public async choseGroupType(group: GroupCreateFields) {
    const groupnameAlert = await this.alertController.create({
      header: 'Group type',
      // TODO: fix types
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      inputs: groupTypesRadioList as unknown as any,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Create',
          handler: (type: GroupType) => this.groupCreate({ ...group, type }),
        },
      ],
    });

    groupnameAlert.present();
  }

  private async groupCreate(group: GroupCreateFields) {
    if (!this.zoneId) {
      return;
    }

    const levelId = this.dmStore.selectedLevel?.value?.id || undefined;
    if (levelId) {
      group.level_id = levelId;
    }
    this.helper.showLoading();
    const groupId = await this.api.groups.create({ ...group }, this.helper);
    this.helper.hideLoading();

    if (groupId) {
      await this.dmStore.fetchGroups({
        zone_id: [this.zoneId],
        level_id: levelId ? [levelId] : undefined,
      });
      this.dmStore.selectGroup(groupId);
    }
  }

  public filterGroups(groups: Group[], text: string) {
    return groups.filter(group => {
      return group.name.toLowerCase().includes(text);
    });
  }

  public getMoreGroups(event: {
    component: IonicSelectableComponent,
    text: string
  }) {
    const scrollEl = innerScrollEl();

    if (!scrollEl) {
      return;
    }

    const scrollVal = scrollEl.scrollTop;

    const text = (event.text || '').trim().toLowerCase();

    if (this.page >= Math.ceil(this.dmStore.groups.length / ITEMS_LIMIT)) {
      event.component.disableInfiniteScroll();
      return;
    }

    getListAsync<Group>(this.dmStore.groups, this.page, ITEMS_LIMIT).subscribe(groups => {
      groups = event.component.items.concat(groups);

      if (text) {
        groups = this.filterGroups(groups, text);
      }

      event.component.items = groups;
      event.component.endInfiniteScroll();
      this.page++;

      if (!text) {
        returnScrollPosition(scrollEl, scrollVal);
      }
    });
  }

  public searchGroups(event: {
    component: IonicSelectableComponent,
    text: string
  }) {
    const text = event.text.trim().toLowerCase();
    event.component.startSearch();

    if (!text) {
      event.component.items = this.dmStore.groups.slice(0, this.page * ITEMS_LIMIT);
      event.component.endSearch();
      scrollToSelected();
      return;
    }

    event.component.items = this.filterGroups(this.dmStore.groups, text);
    event.component.enableInfiniteScroll();
    event.component.endSearch();
  }

}
